import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Instagram } from "lucide-react";
import { api, errMsg, useApi, media } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, Spinner, Empty, UserAvatar } from "@/components/common";
import { EventCard } from "@/components/EventCard";
import { ReviewItem, Stars } from "@/components/Reviews";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Metric = ({ v, l }) => (
  <div><p className="font-display text-3xl font-black">{v ?? 0}</p><p className="font-mono text-[10px] uppercase tracking-widest text-stone-500">{l}</p></div>
);

export default function CreatorProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useApi(`/creators/${id}`);
  if (isLoading) return <Page><Spinner /></Page>;
  if (error || !data) return <Page><Empty title="Profile not found" /></Page>;
  const c = data.creator;
  const isMe = user?.id === c.id;

  const toggleFollow = async () => {
    const was = data.is_following;
    const patch = (f) => qc.setQueryData([`/creators/${id}`], (d) => ({ ...d, is_following: f, followers_count: (d.followers_count || 0) + (f ? 1 : -1) }));
    patch(!was);
    try {
      if (was) await api.delete(`/users/${c.id}/follow`);
      else await api.post(`/users/${c.id}/follow`);
      toast.success(was ? "Unfollowed" : `Following ${c.name}`);
      refetch();
    } catch (e) { patch(was); toast.error(errMsg(e)); }
  };
  const pics = (data.media || []).filter((m) => m.type !== "video");

  return (
    <Page>
      <section className="rise flex flex-col md:flex-row gap-8 md:items-end p-8 rounded-[2rem] bg-white border border-stone-200 relative overflow-hidden">
        <div className="absolute -top-24 -right-16 h-64 w-64 rounded-full bg-[#FF5C00]/15 blur-3xl" />
        <UserAvatar user={c} className="h-28 w-28 sm:h-36 sm:w-36 ring-4 ring-[#FAF8F5]" />
        <div className="relative flex-1 min-w-0">
          <h1 data-testid="creator-name" className="font-display text-4xl sm:text-5xl font-black tracking-tight">{c.name}</h1>
          <p className="text-stone-500">{c.city}{data.leaderboard_rank ? ` · #${data.leaderboard_rank} on the leaderboard` : ""}</p>
          {c.bio && <p className="mt-3 max-w-xl text-stone-700">{c.bio}</p>}
          <div className="mt-3 flex items-center gap-3 text-sm">
            {data.rating > 0 && <span className="flex items-center gap-1.5"><Stars value={data.rating} /> <span className="font-mono">{data.rating} ({data.review_count})</span></span>}
            {c.instagram && <a href={`https://instagram.com/${c.instagram}`} target="_blank" rel="noreferrer" data-testid="creator-instagram" className="inline-flex items-center gap-1 hover:text-[#FF5C00]"><Instagram className="h-4 w-4" />@{c.instagram}</a>}
            {c.tiktok && <a href={`https://tiktok.com/@${c.tiktok}`} target="_blank" rel="noreferrer" data-testid="creator-tiktok" className="hover:text-[#FF5C00]">TikTok @{c.tiktok}</a>}
          </div>
        </div>
        <div className="relative flex flex-col gap-5">
          <div className="flex gap-6"><Metric v={data.events_held} l="Hosted" /><Metric v={data.attended_count} l="Attended" /><Metric v={data.followers_count} l="Followers" /></div>
          {!isMe && (
            <button data-testid="creator-follow-btn" onClick={toggleFollow}
              className={`py-3 rounded-full font-semibold text-sm transition-colors ${data.is_following ? "border border-stone-300 hover:border-red-500 hover:text-red-600" : "bg-[#FF5C00] hover:bg-[#E04E00] text-white"}`}>
              {data.is_following ? "Following" : "Follow"}
            </button>
          )}
        </div>
      </section>

      <Tabs defaultValue="upcoming" className="mt-10">
        <TabsList className="rounded-full bg-stone-200/70 p-1">
          <TabsTrigger value="upcoming" data-testid="creator-tab-upcoming" className="rounded-full">Upcoming ({data.upcoming_events?.length || 0})</TabsTrigger>
          <TabsTrigger value="past" data-testid="creator-tab-past" className="rounded-full">Past</TabsTrigger>
          <TabsTrigger value="photos" data-testid="creator-tab-photos" className="rounded-full">Photos</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="creator-tab-reviews" className="rounded-full">Reviews</TabsTrigger>
        </TabsList>
        <TabsContent value="upcoming" className="mt-6">
          {data.upcoming_events?.length ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{data.upcoming_events.map((e, i) => <EventCard key={e.id} event={{ ...e, creator: e.creator || c }} index={i} />)}</div> : <Empty title="No upcoming events" />}
        </TabsContent>
        <TabsContent value="past" className="mt-6">
          {data.past_events?.length ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{data.past_events.slice(0, 12).map((e, i) => <EventCard key={e.id} event={{ ...e, creator: e.creator || c }} index={i} />)}</div> : <Empty title="No past events" />}
        </TabsContent>
        <TabsContent value="photos" className="mt-6">
          {pics.length ? <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{pics.map((m) => <img key={m.id} src={media(m.url)} alt="" onError={(ev) => (ev.currentTarget.style.display = "none")} className="aspect-square rounded-2xl object-cover bg-stone-200" />)}</div> : <Empty title="No photos yet" />}
        </TabsContent>
        <TabsContent value="reviews" className="mt-6 max-w-2xl">
          {data.reviews?.length ? data.reviews.map((r) => <ReviewItem key={r.id} r={r} />) : <Empty title="No reviews yet" />}
        </TabsContent>
      </Tabs>
    </Page>
  );
}
